import { useId, type CSSProperties } from "react";
import { annualDistance } from "../calc";
import { DECIMALS_DISTANCE, MAX_DISTANCE, MAX_PROFILE_NAME, TIME_PERIODS } from "../constants";
import { PERIOD_LABELS, profilePanel as copy, sections } from "../content";
import { useFmt } from "../fmt";
import type { Profile, TimePeriod } from "../types";
import { NumberInput } from "./NumberInput";

interface Props {
  profile: Profile;
  onChange: (patch: Partial<Profile>) => void;
}

export function ProfilePanel({ profile: p, onChange }: Props) {
  const fmt = useFmt();
  const headingId = useId();
  const distanceId = useId();
  const splitLabel = (pct: number) => `${Math.round(pct)}% · ${fmt.dist((annualDistance(p) * pct) / 100)}`;
  const home = splitLabel(p.homeShare);
  const away = splitLabel(100 - p.homeShare);

  return (
    <section className="panel profile-panel" aria-labelledby={headingId}>
      <h3 className="sr-only" id={headingId}>
        {p.name || copy.fallbackName}
      </h3>
      <div className="title-row">
        <input
          type="text"
          value={p.name}
          maxLength={MAX_PROFILE_NAME}
          placeholder={copy.namePlaceholder}
          aria-label={copy.nameLabel}
          onChange={(e) => onChange({ name: e.target.value })}
        />
      </div>

      <h4 className="eyebrow section-label">{sections.distance}</h4>
      <div className="ws-row">
        <label className="ws-lit" htmlFor={distanceId}>
          {copy.totalPrefix}
        </label>
        <NumberInput
          id={distanceId}
          className="ws-amount"
          style={{ width: 100 }}
          value={p.distanceAmount}
          decimals={DECIMALS_DISTANCE}
          max={MAX_DISTANCE}
          onChange={(n) => onChange({ distanceAmount: n })}
        />
        <span className="ws-lit">{fmt.unit}</span>
        <span className="ws-lit">{copy.perLabel}</span>
        <select
          className="ws-period"
          aria-label={copy.periodLabel}
          value={p.distancePeriod}
          onChange={(e) => onChange({ distancePeriod: e.target.value as TimePeriod })}
        >
          {TIME_PERIODS.map((per) => (
            <option key={per} value={per}>
              {PERIOD_LABELS[per]}
            </option>
          ))}
        </select>
      </div>

      <h4 className="eyebrow section-label">{sections.split}</h4>
      <div className="split-row">
        {/* The slider's aria-valuetext carries the same figures, so these are decoration. */}
        <div className="split-labels" aria-hidden="true">
          <span className="split-inline">
            {copy.home} <b>{home}</b>
          </span>
          <span className="split-inline">
            {copy.away} <b>{away}</b>
          </span>
        </div>
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={p.homeShare}
          style={{ "--pct": `${p.homeShare}%` } as CSSProperties}
          aria-label={copy.sliderLabel}
          aria-valuetext={copy.sliderValueText(home, away)}
          onChange={(e) => onChange({ homeShare: Number(e.target.value) })}
        />
      </div>
    </section>
  );
}
